import { useEffect, useState } from 'react'

/**
 * Плотность интерфейса кассы.
 *
 * `touch` — POS-терминал 10.1" (1024–1440 px) или устройство с грубым
 * указателем: увеличенные цели касания, крупные плитки, safe-area.
 * `mouse` — ноутбук/десктоп с мышью.
 */
export type PosDensity = 'touch' | 'mouse'

/** Нижняя граница «терминального» диапазона ширины. */
export const POS_TOUCH_MIN_WIDTH = 1024
/** Верхняя граница: шире — обычный десктоп с мышью. */
export const POS_TOUCH_MAX_WIDTH = 1440

function detectDensity(): PosDensity {
  if (typeof window === 'undefined') return 'mouse'
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false
  const width = window.innerWidth
  const terminalWidth = width >= POS_TOUCH_MIN_WIDTH && width <= POS_TOUCH_MAX_WIDTH
  return coarse || terminalWidth ? 'touch' : 'mouse'
}

/** Реактивно отслеживает плотность кассы. */
export function usePosDensity(): PosDensity {
  const [density, setDensity] = useState<PosDensity>(detectDensity)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const update = () => setDensity(detectDensity())
    update()
    window.addEventListener('resize', update)
    const media = window.matchMedia?.('(pointer: coarse)')
    media?.addEventListener?.('change', update)
    return () => {
      window.removeEventListener('resize', update)
      media?.removeEventListener?.('change', update)
    }
  }, [])

  return density
}
